/**
 * List items page: pick a product, view details, load MLB preset, edit listing preview, publish to Mercado Livre.
 * Composes useListingPage and subcomponents; state in global store.
 */
import React from 'react';
import { Container, Form, Button, Spinner } from 'react-bootstrap';
import { useListingPage } from './useListingPage';
import { DropdownField, InfoTableCard } from '../common';
import MlbPresetTable from './MlbPresetTable';
import ListingPreviewCard from './ListingPreviewCard';
import PickListingSuppliersModal from './PickListingSuppliersModal';

const ListingComponent = () => {
  const {
    productDropdownRef,
    productSearchQuery,
    showProductDropdown,
    setListItemsPage,
    selectedProduct,
    productsLoading,
    filteredProducts,
    handleProductSearchChange,
    handleSelectProduct,
    mlbDetails,
    mlbError,
    mlbLoading,
    publishLoading,
    publishError,
    publishSuccess,
    handleLoadMlbPreset,
    handlePublishToMlb,
    basicInfoRows,
    classificationRows,
    listingPictures,
    listingPictureIndex,
    setListingPictureIndex,
    effectiveListing,
    editingField,
    editingValue,
    setEditingValue,
    handleStartEdit,
    handleSaveEdit,
    eligibleProductSuppliers,
    showPickListingSuppliersModal,
    handleOpenPickSuppliersModal,
    handleSavePickSuppliersModal,
    handleClosePickSuppliersModal,
    initialSelectionForModal
  } = useListingPage();

  return (
    <div className="page-dark">
      <Container>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <h1 className="page-title mb-0">List items</h1>
          {selectedProduct && (
            <Button
              variant="primary"
              onClick={mlbDetails !== null ? handlePublishToMlb : handleLoadMlbPreset}
              disabled={mlbLoading || publishLoading}
              className="d-flex align-items-center gap-2"
            >
              {mlbLoading ? (
                <>
                  <Spinner animation="border" size="sm" aria-hidden />
                  <span>Loading…</span>
                </>
              ) : publishLoading ? (
                <>
                  <Spinner animation="border" size="sm" aria-hidden />
                  <span>Publishing…</span>
                </>
              ) : mlbDetails !== null ? (
                'Publish to MLB'
              ) : (
                'Load MLB Preset'
              )}
            </Button>
          )}
        </div>
        <p className="page-subtitle">Search and select a product to view its details.</p>
        {publishError && (
          <div className="alert alert-danger mb-3" role="alert">{publishError}</div>
        )}
        {publishSuccess && (
          <div className="alert alert-success mb-3" role="alert">{publishSuccess}</div>
        )}
        <div className="page-content">
          <Form.Group className="mb-4">
            <Form.Label>Product</Form.Label>
            <DropdownField
              ref={productDropdownRef}
              value={productSearchQuery}
              onChange={handleProductSearchChange}
              placeholder="Search by product name..."
              disabled={productsLoading}
              loading={productsLoading}
              loadingMessage="Loading products..."
              showDropdown={showProductDropdown && !productsLoading}
              options={filteredProducts}
              emptyMessage={productSearchQuery.trim() ? 'No matching products' : 'Type to search products'}
              getLabel={(p) => p.name || '(Unnamed)'}
              getItemKey={(p) => p.id}
              onSelect={handleSelectProduct}
              onFocus={() => setListItemsPage((prev) => ({ ...prev, showProductDropdown: true }))}
              onClick={() => setListItemsPage((prev) => ({ ...prev, showProductDropdown: true }))}
              id="listing-product-dropdown"
            />
          </Form.Group>
        </div>

        {selectedProduct && (
          <div className="row g-3">
            <div className="col-12 col-lg-5">
              <InfoTableCard title="Basic information" rows={basicInfoRows} />
              <InfoTableCard title="Classification" rows={classificationRows} />
              {mlbError && (
                <div className="alert alert-danger mb-3">{mlbError}</div>
              )}
              <MlbPresetTable mlbDetails={mlbDetails} />
            </div>
            <div className="col-12 col-lg-7">
              {effectiveListing && mlbDetails !== null && (
                <>
                  <PickListingSuppliersModal
                    show={showPickListingSuppliersModal}
                    onHide={handleClosePickSuppliersModal}
                    eligibleProductSuppliers={eligibleProductSuppliers}
                    initialSelection={initialSelectionForModal}
                    onSave={handleSavePickSuppliersModal}
                  />
                  <ListingPreviewCard
                    effectiveListing={effectiveListing}
                    listingPictures={listingPictures}
                    listingPictureIndex={listingPictureIndex}
                    setListingPictureIndex={setListingPictureIndex}
                    editingField={editingField}
                    editingValue={editingValue}
                    setEditingValue={setEditingValue}
                    handleStartEdit={handleStartEdit}
                    handleSaveEdit={handleSaveEdit}
                    headerAction={
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="btn-card-header"
                        onClick={handleOpenPickSuppliersModal}
                        disabled={eligibleProductSuppliers.length === 0}
                      >
                        Pick listing suppliers
                      </Button>
                    }
                  />
                </>
              )}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default ListingComponent;
